import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent } from '@next/core-lib/components';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { SelectedCustomer, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { BehaviorSubject, EMPTY, Observable, Subscription } from 'rxjs';
import { catchError, delay, repeat, take, withLatestFrom } from 'rxjs/operators';
import {
  GenderTypes,
  GeneticLineType,
  TerminalBoar,
} from 'src/app/feed-advice/models/feed-advice.model';
import { AnimalTypeOption, NedapInstallation } from 'src/app/settings/models/third-parties.model';
import { NedapIntegrationService } from 'src/app/settings/services/nedap-integration.service';
import { updateNedapInstallationMetaDataRequest } from 'src/app/settings/store/actions/settings.actions';
import { getNedapInstallations } from 'src/app/settings/store/selectors/settings.selectors';

@Component({
  selector: 'app-nedap-installation-overview-redirect',
  templateUrl: 'nedap-installation-overview.component.html',
  imports: [
    PageToolbarComponent,
    NgIf,
    TableComponent,
    MatSelect,
    NgFor,
    MatOption,
    MatButton,
    MatIcon,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class NedapInstallationOverviewComponent implements OnInit, OnDestroy {
  constructor(
    private readonly store: Store<RootState>,
    @Inject(EnvironmentInjector)
    private readonly env: {
      NEDAP_BASE_URL: string;
      NEDAP_CLIENT_ID: string;
      NEDAP_REDIRECT_URI: string;
    },
    private readonly localeService: LocaleService,
    private readonly nedapIntegrationService: NedapIntegrationService,
  ) {}

  @ViewChild('animalTypeTemplate') animalTypeTemplate: TemplateRef<AnimalTypeOption>;
  @ViewChild('secondaryAnimalTypeTemplate')
  secondaryAnimalTypeTemplate: TemplateRef<AnimalTypeOption>;
  @ViewChild('thirdAnimalTypeTemplate') thirdAnimalTypeTemplate: TemplateRef<AnimalTypeOption>;
  @ViewChild('statusTemplate') statusTemplate: TemplateRef<void>;

  public nedapInstallations$: Observable<NedapInstallation[]> =
    this.store.select(getNedapInstallations);
  public isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  public names: FormArray = new FormArray([]);
  public animalTypes: FormArray = new FormArray([]);
  public secondaryAnimalTypes: FormArray = new FormArray([]);
  public thirdAnimalTypes: FormArray = new FormArray([]);

  public hasData$ = new BehaviorSubject(false);

  public animalTypeOptions: AnimalTypeOption[] = [
    {
      value: 'fattening',
      label: 'feedAdviceType.fattening',
      secondAnimalTypeOptions: [
        {
          value: GenderTypes.BARROW,
          label: 'gender.barrow',
        },
        {
          value: GenderTypes.BOAR,
          label: 'gender.boar',
        },
        {
          value: GenderTypes.GILT,
          label: 'gender.gilt',
        },
      ],
      thirdAnimalTypeOptions: [
        { value: TerminalBoar.DUROC, label: TerminalBoar.DUROC },
        { value: TerminalBoar.PIETRAIN, label: TerminalBoar.PIETRAIN },
        { value: TerminalBoar.TEMPO, label: TerminalBoar.TEMPO },
      ],
    },
    {
      value: 'rearing',
      label: 'feedAdviceType.rearing',
      secondAnimalTypeOptions: [
        {
          value: GenderTypes.BARROW,
          label: 'gender.barrow',
        },
        {
          value: GenderTypes.BOAR,
          label: 'gender.boar',
        },
        {
          value: GenderTypes.GILT,
          label: 'gender.gilt',
        },
      ],
      thirdAnimalTypeOptions: [
        { value: TerminalBoar.DUROC, label: TerminalBoar.DUROC },
        { value: TerminalBoar.PIETRAIN, label: TerminalBoar.PIETRAIN },
        { value: TerminalBoar.TEMPO, label: TerminalBoar.TEMPO },
      ],
    },
    {
      value: 'sowsbreedtofarrow',
      label: 'feedAdviceType.sowsbreedtofarrow',
      secondAnimalTypeOptions: [
        {
          value: GeneticLineType.DANBRED,
          label: GeneticLineType.DANBRED,
        },
        {
          value: GeneticLineType.HYPOR,
          label: GeneticLineType.HYPOR,
        },
        {
          value: GeneticLineType.PIC,
          label: GeneticLineType.PIC,
        },
        {
          value: GeneticLineType.TOPIGS20,
          label: GeneticLineType.TOPIGS20,
        },
        {
          value: GeneticLineType.TOPIGS50,
          label: GeneticLineType.TOPIGS50,
        },
        {
          value: GeneticLineType.TN60,
          label: GeneticLineType.TN60,
        },
        {
          value: GeneticLineType.TN70,
          label: GeneticLineType.TN70,
        },
      ],
    },
    {
      value: 'sowsfarrowtowean',
      label: 'feedAdviceType.sowsfarrowtowean',
      secondAnimalTypeOptions: [
        {
          value: GeneticLineType.DANBRED,
          label: GeneticLineType.DANBRED,
        },
        {
          value: GeneticLineType.HYPOR,
          label: GeneticLineType.HYPOR,
        },
        {
          value: GeneticLineType.PIC,
          label: GeneticLineType.PIC,
        },
        {
          value: GeneticLineType.TOPIGS20,
          label: GeneticLineType.TOPIGS20,
        },
        {
          value: GeneticLineType.TOPIGS50,
          label: GeneticLineType.TOPIGS50,
        },
        {
          value: GeneticLineType.TN60,
          label: GeneticLineType.TN60,
        },
        {
          value: GeneticLineType.TN70,
          label: GeneticLineType.TN70,
        },
      ],
    },
    {
      value: 'sowsweantobreed',
      label: 'feedAdviceType.sowsweantobreed',
      secondAnimalTypeOptions: [
        {
          value: GeneticLineType.DANBRED,
          label: GeneticLineType.DANBRED,
        },
        {
          value: GeneticLineType.HYPOR,
          label: GeneticLineType.HYPOR,
        },
        {
          value: GeneticLineType.PIC,
          label: GeneticLineType.PIC,
        },
        {
          value: GeneticLineType.TOPIGS20,
          label: GeneticLineType.TOPIGS20,
        },
        {
          value: GeneticLineType.TOPIGS50,
          label: GeneticLineType.TOPIGS50,
        },
        {
          value: GeneticLineType.TN60,
          label: GeneticLineType.TN60,
        },
        {
          value: GeneticLineType.TN70,
          label: GeneticLineType.TN70,
        },
      ],
    },
    {
      value: 'weanedpiglet',
      label: 'feedAdviceType.weanedpiglet',
      secondAnimalTypeOptions: [
        {
          value: GenderTypes.BARROW,
          label: 'gender.barrow',
        },
        {
          value: GenderTypes.BOAR,
          label: 'gender.boar',
        },
        {
          value: GenderTypes.GILT,
          label: 'gender.gilt',
        },
      ],
      thirdAnimalTypeOptions: [
        { value: TerminalBoar.DUROC, label: TerminalBoar.DUROC },
        { value: TerminalBoar.PIETRAIN, label: TerminalBoar.PIETRAIN },
        { value: TerminalBoar.TEMPO, label: TerminalBoar.TEMPO },
      ],
    },
  ];

  public nedapInstallationsTableConfig: TableConfig;

  private nedapInstallationsSubscriptions: Subscription[] = [];

  ngOnInit(): void {
    this.nedapInstallationsSubscriptions.push(
      this.nedapInstallations$.subscribe((nedapInstallations) => {
        this.nedapInstallationsTableConfig = {
          dataSource: new MatTableDataSource<NedapInstallation>(nedapInstallations),
          columns: [
            {
              id: 'installationId',
              type: TableColumnTypes.STRING,
              label: 'Id',
            },
            {
              id: 'installationSerialNumber',
              type: TableColumnTypes.STRING,
              label: this.localeService.translate(
                'settings.third-parties.nedap-installation-serial-number',
              ),
            },
            {
              id: 'name',
              type: TableColumnTypes.INPUT_STRING,
              inputConfig: {
                formArray: this.names,
              },
              label: this.localeService.translate('settings.third-parties.nedap-installation-name'),
            },
            {
              id: 'animalType',
              type: TableColumnTypes.TEMPLATE,
              template: this.animalTypeTemplate,
              inputConfig: {
                formArray: this.animalTypes,
              },
              label: this.localeService.translate(
                'settings.third-parties.nedap-installation-animal-type',
              ),
            },
            {
              id: 'secondaryAnimalType',
              type: TableColumnTypes.TEMPLATE,
              template: this.secondaryAnimalTypeTemplate,
              inputConfig: {
                formArray: this.secondaryAnimalTypes,
              },
              label: this.localeService.translate(
                'settings.third-parties.nedap-installation-second-animal-type',
              ),
            },
            {
              id: 'thirdAnimalType',
              type: TableColumnTypes.TEMPLATE,
              template: this.thirdAnimalTypeTemplate,
              inputConfig: {
                formArray: this.thirdAnimalTypes,
              },
              label: this.localeService.translate(
                'settings.third-parties.nedap-installation-third-animal-type',
              ),
            },
            {
              id: 'status',
              type: TableColumnTypes.TEMPLATE,
              template: this.statusTemplate,
              label: this.localeService.translate(
                'settings.third-parties.nedap-installation-status',
              ),
            },
          ],
          selectColumn: false,
          pagination: false,
        };
        if (nedapInstallations?.length > 0) {
          this.hasData$.next(true);
        }
      }),
    );

    this.nedapInstallationsSubscriptions.push(
      this.nedapIntegrationService
        .getInstallations()
        .pipe(
          delay(3000),
          repeat(),
          catchError((error) => {
            console.error(error);
            return EMPTY;
          }),
        )
        .subscribe((installations: NedapInstallation[]) => {
          let dataUpdated = false;
          const data = this.nedapInstallationsTableConfig.dataSource.data.map(
            (e: NedapInstallation) => Object.assign({}, e),
          );
          installations.forEach((updatedInstallation) => {
            const existing = data.find(
              (r: NedapInstallation) => r.installationId === updatedInstallation.installationId,
            );
            if (existing) {
              if (existing.status !== updatedInstallation.status) {
                // Status update has change for this installation. Update it in the table data.
                dataUpdated = true;
                existing.status = updatedInstallation.status;
              }
            } else {
              // This is a new installation, add it to the current table data.
              data.push({ ...updatedInstallation });
              this.hasData$.next(true);
              dataUpdated = true;
            }
          });

          if (dataUpdated) {
            this.nedapInstallationsTableConfig.dataSource.data = data;
          }
        }),
    );
  }

  ngOnDestroy(): void {
    if (this.nedapInstallationsSubscriptions?.length > 0) {
      {
        this.nedapInstallationsSubscriptions.forEach((s) => s?.unsubscribe());
      }
    }
  }

  private selectedCustomer$: Observable<SelectedCustomer> = this.store.select(
    UserSelectors.selectCustomer,
  );

  private selectedLocation$: Observable<SelectedLocation> = this.store.select(
    UserSelectors.selectLocation,
  );

  public getSelectedAnimalType(row: NedapInstallation): AnimalTypeOption {
    return this.animalTypeOptions.find((a) => a.value === row.animalType);
  }

  public getSelectedSecondaryAnimalType(row: NedapInstallation): AnimalTypeOption {
    return this.animalTypeOptions
      .find((a) => a.value === row.animalType)
      ?.secondAnimalTypeOptions?.find((a) => a.value === row.secondaryAnimalType);
  }

  public getSelectedThirdAnimalType(row: NedapInstallation): AnimalTypeOption {
    return this.animalTypeOptions
      .find((a) => a.value === row.animalType)
      ?.thirdAnimalTypeOptions?.find((a) => a.value === row.thirdAnimalType);
  }

  public selectAnimalType(selection: MatSelectChange, nedapInstallation: NedapInstallation) {
    const data = this.nedapInstallationsTableConfig.dataSource.data.map((e: NedapInstallation) =>
      Object.assign({}, e),
    );
    const existing = data.find(
      (r: NedapInstallation) => r.installationId === nedapInstallation.installationId,
    );
    existing.animalType = selection.value.value;
    existing.secondaryAnimalType = undefined;
    existing.thirdAnimalType = undefined;
    this.nedapInstallationsTableConfig.dataSource.data = data;
  }

  public selectSecondaryAnimalType(
    selection: MatSelectChange,
    nedapInstallation: NedapInstallation,
  ) {
    const data = this.nedapInstallationsTableConfig.dataSource.data.map((e: NedapInstallation) =>
      Object.assign({}, e),
    );
    const existing = data.find(
      (r: NedapInstallation) => r.installationId === nedapInstallation.installationId,
    );
    existing.secondaryAnimalType = selection.value.value;
    this.nedapInstallationsTableConfig.dataSource.data = data;
  }

  public selectThirdAnimalType(selection: MatSelectChange, nedapInstallation: NedapInstallation) {
    const data = this.nedapInstallationsTableConfig.dataSource.data.map((e: NedapInstallation) =>
      Object.assign({}, e),
    );
    const existing = data.find(
      (r: NedapInstallation) => r.installationId === nedapInstallation.installationId,
    );
    existing.thirdAnimalType = selection.value.value;
    this.nedapInstallationsTableConfig.dataSource.data = data;
  }

  public handleAddInstallation() {
    this.selectedCustomer$
      .pipe(withLatestFrom(this.selectedLocation$), take(1))
      .subscribe(([customer, location]) => {
        // eslint-disable-next-line max-len
        const url = `${this.env.NEDAP_BASE_URL}/oauth/authorize?response_type=code&client_id=${this.env.NEDAP_CLIENT_ID}&scope=account weights feeding_animal_summaries feeding_location_summaries&redirect_uri=${this.env.NEDAP_REDIRECT_URI}&state=${customer.id}_${location.id}`;
        window.open(url, '_blank');
      });
  }

  public save() {
    this.nedapInstallationsTableConfig.dataSource.data.forEach(
      (installation: NedapInstallation, i: number) => {
        this.store.dispatch(
          updateNedapInstallationMetaDataRequest({
            nedapInstallation: {
              installationId: installation.installationId,
              name: this.names.controls[i].value,
              animalType: installation.animalType,
              secondaryAnimalType: installation.secondaryAnimalType,
              thirdAnimalType: installation.thirdAnimalType,
            },
          }),
        );
      },
    );
  }
}
